
import * as THREE from "../../libs/three.js/build/three.module.js";
import { Volume, BoxVolume } from "./Volume.js";
import { Utils } from "../utils.js";
import { EventDispatcher } from "../EventDispatcher.js";
import { ShowMode } from "../defines.js";

export class VolumeTool extends EventDispatcher {
	constructor(viewer) {
		super();

		this.viewer = viewer;
		this.renderer = viewer.renderer;

		this.addEventListener('start_inserting_volume', e => {
			this.viewer.dispatchEvent({
				type: 'cancel_insertions'
			});
		});

		this.scene = new THREE.Scene();
		this.scene.name = 'scene_volume';

		//
		const hemiLight1 = new THREE.HemisphereLight(0xffffff, 0x444444, 5);
		hemiLight1.position.set(0, 20000, 0);
		this.scene.add(hemiLight1);

		const dirLight1 = new THREE.DirectionalLight(0xffffff, 5);
		dirLight1.position.set(0, 20000, 10000);
		dirLight1.castShadow = true;
		dirLight1.shadow.camera.top = 180;
		dirLight1.shadow.camera.bottom = - 100;
		dirLight1.shadow.camera.left = - 120;
		dirLight1.shadow.camera.right = 120;
		this.scene.add(dirLight1);

		var ambientLight = new THREE.AmbientLight(0xffffff, 0.9);
		this.scene.add(ambientLight);
		//

		this.viewer.inputHandler.registerInteractiveScene(this.scene);

		this.onRemove = e => {
			// 断开 viewer 反向引用（setWebMaterial 需要它拿 CSS3D 层）。
			// 注意 Scene.removeVolume 会在派发 volume_removed 之后再调 volume.dispose()，
			// 而 dispose 内部要靠 this.viewer 找到 CSS3D 层来移除 iframe，
			// 所以这里不能直接清空——改为在 dispose 完成后由 Scene 侧自然失效。
			this.scene.remove(e.volume);
		};

		this.onAdd = e => {
			// 注入 viewer 反向引用：YDBoxVolume 的网页资源(id=9)需要通过它拿到 viewer 的 CSS3D 层
			e.volume.viewer = this.viewer;
			this.scene.add(e.volume);
		};

		for (let volume of viewer.scene.volumes) {
			this.onAdd({ volume: volume });
		}

		this.viewer.inputHandler.addEventListener('delete', e => {
			let volumes = e.selection.filter(e => (e instanceof Volume));
			volumes.forEach(e => this.viewer.scene.removeVolume(e));
		});

		viewer.addEventListener("update", this.update.bind(this));
		viewer.addEventListener("render.pass.scene", e => this.render(e));
		// 资源盒 box 在 clearDepth 前渲染(参与深度，被点云/高斯遮挡)；overlay pass
		// (render.pass.scene) 跳过资源盒 box 避免重画覆盖遮挡效果。
		viewer.addEventListener("render.pass.depth", e => this.renderDepth(e));
		viewer.addEventListener("scene_changed", this.onSceneChange.bind(this));

		viewer.scene.addEventListener('volume_added', this.onAdd);
		viewer.scene.addEventListener('volume_removed', this.onRemove);
	}

	onSceneChange(e) {
		if (e.oldScene) {
			e.oldScene.removeEventListeners('volume_added', this.onAdd);
			e.oldScene.removeEventListeners('volume_removed', this.onRemove);
		}

		e.scene.addEventListener('volume_added', this.onAdd);
		e.scene.addEventListener('volume_removed', this.onRemove);
	}

	startInsertion(args = {}) {
		let volume;
		if (args.type) {
			volume = new args.type();
		} else {
			volume = new BoxVolume();
		}

		volume.clip = args.clip || false;
		volume.name = args.name || 'Volume';
		// 放置时正面朝向相机（方案A快照）。默认 false 保持原轴对齐；调用方可传 true 开启。
		volume.faceToCameraOnPlace = args.faceToCameraOnPlace || false;
		// 简化版：放置时仅水平偏转对准相机（绕世界 +Z 纯 yaw，保持竖直、不倾斜）。
		volume.faceToCameraSimpleOnPlace = args.faceToCameraSimpleOnPlace || false;
		// 跟随鼠标阶段逐帧朝向相机（默认 false，仅落位瞬间朝向）。
		volume.faceToCameraWhilePlacing = args.faceToCameraWhilePlacing || false;

		this.dispatchEvent({
			type: 'start_inserting_volume',
			volume: volume
		});

		this.viewer.scene.addVolume(volume);
		this.scene.add(volume);

		let cancel = {
			callback: null
		};

		let drag = e => {
			let camera = this.viewer.scene.getActiveCamera();
			let I = Utils.getMouseIntersection(
				e.drag.end,
				camera,
				this.viewer,
				{ pickClipped: false });

			if (I) {
				volume.position.copy(I.location);

				let wp = volume.getWorldPosition(new THREE.Vector3()).applyMatrix4(camera.matrixWorldInverse);
				// let pp = new THREE.Vector4(wp.x, wp.y, wp.z).applyMatrix4(camera.projectionMatrix);
				let w = Math.abs((wp.z / 5));
				volume.scale.set(w, w, w);
			}
		};

		let drop = e => {
			volume.removeEventListener('drag', drag);
			volume.removeEventListener('drop', drop);

			// 方案A（放置快照）：落位瞬间让盒子正面朝向相机，仅此一次，不持续跟随。
			// faceToCameraOnPlace: 完整正对（含俯仰）；faceToCameraSimpleOnPlace: 简化(仅水平偏转、保持竖直)。
			if (volume.faceToCameraOnPlace && typeof volume.faceToCamera === 'function') {
				volume.faceToCamera(this.viewer.scene.getActiveCamera());
			} else if (volume.faceToCameraSimpleOnPlace && typeof volume.faceToCameraYaw === 'function') {
				volume.faceToCameraYaw(this.viewer.scene.getActiveCamera());
			}

			cancel.callback();
		};

		cancel.callback = e => {
			volume.removeEventListener('drag', drag);
			volume.removeEventListener('drop', drop);
			this.viewer.removeEventListener('cancel_insertions', cancel.callback);
		};

		volume.addEventListener('drag', drag);
		volume.addEventListener('drop', drop);
		this.viewer.addEventListener('cancel_insertions', cancel.callback);

		this.viewer.inputHandler.startDragging(volume);

		return volume;
	}

	update() {
		if (!this.viewer.scene) {
			return;
		}

		let camera = this.viewer.scene.getActiveCamera();
		let renderAreaSize = this.viewer.renderer.getSize(new THREE.Vector2());
		let clientWidth = renderAreaSize.width;
		let clientHeight = renderAreaSize.height;

		// 放置拖拽阶段逐帧让正面朝向相机。判据用 inputHandler.drag.object：
		// drop 时 removeEventListener('drag') 会让该盒子不再成为 drag.object
		// (InputHandler 按 _listeners['drag'].length > 0 筛选拖拽目标)，
		// 且 drag 在 onMouseUp 后置 null，故此处不会误伤已放置盒子的 gizmo 旋转。
		// 不能改用 volume 的 drag 事件：drag 只由 mousemove 驱动，鼠标静止时
		// 滚轮仍会缩放相机，那时朝向也必须跟着更新。
		const dragState = this.viewer.inputHandler && this.viewer.inputHandler.drag;
		const placing = dragState && dragState.object;
		// _panoImmersive 期间跳过：enterPanorama 快照了盒子世界朝向与基准偏航，
		// 逐帧改朝向会让快照失配。该字段未在构造函数初始化，undefined 取反为 true。
		if (placing && placing.faceToCameraWhilePlacing && !placing._panoImmersive) {
			if (placing.faceToCameraOnPlace && typeof placing.faceToCamera === 'function') {
				placing.faceToCamera(camera);
			} else if (typeof placing.faceToCameraYaw === 'function') {
				placing.faceToCameraYaw(camera);
			}
		}

		let volumes = this.viewer.scene.volumes;
		for (let volume of volumes) {
			if (typeof volume._updateSpawnMarker === 'function') {
				// 出生点(id=11)逐帧动画：面向相机 + 芒线自转 + 呼吸
				volume._updateSpawnMarker(camera);
			}
			let label = volume.label;
			if (!label) continue
			{

				let distance = label.position.distanceTo(camera.position);
				let pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);

				let scale = (70 / pr);
				label.scale.set(scale, scale, scale);
			}

			let calculatedVolume = volume.getVolume();
			calculatedVolume = calculatedVolume / Math.pow(this.viewer.lengthUnit.unitspermeter, 3) * Math.pow(this.viewer.lengthUnitDisplay.unitspermeter, 3);  //convert to cubic meters then to the cubic display unit
			let text = Utils.addCommas(calculatedVolume.toFixed(3)) + ' ' + this.viewer.lengthUnitDisplay.code + '\u00B3';
			label.setText(text);
		}
	}

	render(params) {
		const renderer = this.viewer.renderer;

		const oldTarget = renderer.getRenderTarget();
		if (params.renderTarget) {
			renderer.setRenderTarget(params.renderTarget);
		}

		// 所有 volume 的 box+frame+资源盒模型已在 render.pass.depth 渲染(参与深度遮挡)，
		// overlay pass 全部跳过避免重画覆盖。overlay pass 实际只渲染 clickBtns 等附属 UI
		// (frame/box/模型都在 depth pass 画过了)。
		const hidden = [];
		for (const volume of this.viewer.scene.volumes) {
			if (!volume) continue;
			// frame：已在 depth pass 渲染(黑色边框有遮挡)，overlay 跳过避免重画
			if (volume.frame && volume.frame.visible) {
				volume.frame.visible = false;
				hidden.push(volume.frame);
			}
			// box(面)：已在 depth pass 渲染(有遮挡)，overlay 跳过
			if (volume.box && volume.box.visible) {
				volume.box.visible = false;
				hidden.push(volume.box);
			}
			// 模型子对象(id=4)同样跳过 overlay，避免重画覆盖遮挡
			for (const child of volume.children) {
				if (child && child.name === "yd_fbx_model" && child.visible) {
					child.visible = false;
					hidden.push(child);
				} else if (child && child.name === "ydbox_pano_sphere" && child.visible) {
					// 全景球体(id=10)：overlay 跳过，避免再次无遮挡绘制覆盖在点云之上
					child.visible = false;
					hidden.push(child);
				} else if (child && child.name === "ydbox_spawn_marker" && child.visible) {
					// 出生点(id=11)：overlay 跳过，depth pass 已画
					child.visible = false;
					hidden.push(child);
				}
			}
		}

		renderer.render(this.scene, this.viewer.scene.getActiveCamera());

		for (const box of hidden) box.visible = true;
		renderer.setRenderTarget(oldTarget);
	}

	// depth pass：在 clearDepth 前渲染资源盒(已挂载资源)的 box 及挂载的 3D 模型，
	// 让其参与深度测试被点云/高斯遮挡。box/模型材质本身 depthTest=true、depthWrite=false，
	// 此处只测深度(被场景遮挡)，不写深度(避免 box 的面挡住盒内模型)。空盒不进此 pass。
	// 渲染整个 this.scene(含灯光)：3D 模型用 PBR/Phong 受光材质，单独 render 不带灯光
	// 会纯黑，故走 scene 渲染让 HemisphereLight/DirectionalLight/AmbientLight 生效。
	renderDepth(params) {
		const renderer = this.viewer.renderer;
		const camera = this.viewer.scene.getActiveCamera();
		const oldTarget = renderer.getRenderTarget();
		if (params && params.renderTarget) {
			renderer.setRenderTarget(params.renderTarget);
		}

		// 用 visible 开关控制：所有 volume 的 box+frame visible=true(参与深度，被点云/
		// 高斯遮挡)，资源盒的 3D 模型也 visible=true。渲染整个 this.scene(含灯光)，让 PBR
		// 模型受光不致纯黑。box/frame 走 clearDepth 前渲染以恢复原版"面与边框有遮挡"的行为
		// (选中黄色边框是 TransformationTool gizmo，在 clearDepth 后 overlay)。
		const toggled = [];
		for (const volume of this.viewer.scene.volumes) {
			if (!volume) continue;
			const isRes = !!volume.res;
			// box(面)：始终参与深度(被遮挡)，与原版 potreejs 一致
			if (volume.box) {
				toggled.push({ obj: volume.box, orig: volume.box.visible });
				volume.box.visible = true;
			}
			// frame(边框)：始终参与深度(被遮挡)，与原版 potreejs 一致
			if (volume.frame) {
				toggled.push({ obj: volume.frame, orig: volume.frame.visible });
				volume.frame.visible = true;
			}
			for (const child of volume.children) {
				if (child && child.name === "yd_fbx_model") {
					toggled.push({ obj: child, orig: child.visible });
					child.visible = isRes;
				} else if (child && child.name === "ydbox_pano_sphere") {
					// 全景球体(id=10)：参与深度(被点云/高斯遮挡)，与 3D 模型同处理
					toggled.push({ obj: child, orig: child.visible });
					child.visible = isRes;
				} else if (child && child.name === "ydbox_spawn_marker") {
					// 出生点(id=11)：参与深度(被点云/高斯遮挡)
					toggled.push({ obj: child, orig: child.visible });
					child.visible = isRes;
				}
			}
		}

		renderer.render(this.scene, camera);

		for (const t of toggled) t.obj.visible = t.orig;
		renderer.setRenderTarget(oldTarget);
	}

}
